// Game-specific types

export interface Vector2 {
  x: number;
  y: number;
}

export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

// River Rush Types
export type BiomeType = 'kelp-forest' | 'amazon-river' | 'european-stream' | 'asian-mangroves' | 'african-wetlands';

export interface RiverRushOtter {
  id: string;
  name: string;
  speciesId: string;
  ability: string;
  abilityDescription: string;
  unlockCondition: string;
  unlocked: boolean;
}

export interface FoodItem {
  id: string;
  name: string;
  points: number;
  preferredBy: string[]; // species IDs
  biomes: BiomeType[];
  sprite: string;
}

export interface Obstacle {
  id: string;
  name: string;
  damage: number;
  biomes: BiomeType[];
  sprite: string;
  canBreak: boolean; // Can be broken with Tool Time power-up
}

export interface PowerUp {
  id: string;
  name: string;
  description: string;
  duration: number; // seconds
  sprite: string;
  effect: 'raft' | 'tool' | 'speed' | 'appetite';
}

export interface RiverRushState {
  score: number;
  distance: number;
  health: number;
  maxHealth: number;
  combo: number;
  speed: number;
  activePowerUp: PowerUp | null;
  powerUpEndTime: number;
  isPaused: boolean;
  isGameOver: boolean;
  selectedOtter: RiverRushOtter;
  currentBiome: BiomeType;
}

export interface RiverRushProgress {
  highScore: number;
  totalDistance: number;
  totalFoodCollected: number;
  gamesPlayed: number;
  unlockedOtters: string[];
  fishTokens: number;
  upgrades: {
    thickerFur: number; // 0-2
    biggerAppetite: number; // 0-3
    quickReflexes: number; // 0-3
    luckyOtter: number; // 0-3
  };
}

// Rescue Mission Types
export type RescueScenarioType = 'oil-spill' | 'orphaned-pup' | 'net-entanglement' | 'pollution-sickness' | 'habitat-loss';

export type MiniGameType = 'oil-cleaning' | 'feeding' | 'swimming-lessons' | 'net-removal' | 'habitat-matching' | 'release-prep';

export interface RescueCase {
  id: string;
  scenarioType: RescueScenarioType;
  otterName: string;
  speciesId: string;
  story: string;
  stages: RescueStage[];
  completed: boolean;
  currentStage: number;
}

export interface RescueStage {
  id: string;
  name: string;
  description: string;
  miniGame?: MiniGameType;
  requirements?: StageRequirement[];
  completed: boolean;
}

export interface StageRequirement {
  type: 'score' | 'time' | 'accuracy';
  target: number;
}

export interface RescuedOtterCard {
  id: string;
  name: string;
  speciesId: string;
  scenarioType: RescueScenarioType;
  rescuedDate: string;
  recoveryDays: number;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
}

export interface RescueMissionProgress {
  rescuedOtters: RescuedOtterCard[];
  totalRescues: number;
  currentCases: RescueCase[];
  facilityUpgrades: {
    medicalBay: number; // 0-3
    nursery: number; // 0-3
    trainingPool: number; // 0-3
    researchLab: number; // 0-3
  };
  rescuerRank: 'volunteer' | 'junior-keeper' | 'senior-keeper' | 'rehabilitation-expert' | 'conservation-hero';
  stars: number;
}

// Mini-game specific types
export interface OilCleaningState {
  cleanPercentage: number;
  stressLevel: number;
  maxStress: number;
  oilPatches: OilPatch[];
  currentTool: 'soap' | 'sponge' | 'rinse';
}

export interface OilPatch {
  id: string;
  x: number;
  y: number;
  radius: number;
  cleaned: boolean;
}

export interface FeedingGameState {
  grid: FeedingTile[][];
  targetCalories: number;
  currentCalories: number;
  movesLeft: number;
  targetSpecies: string;
}

export interface FeedingTile {
  type: 'fish' | 'crab' | 'shrimp' | 'squid' | 'urchin' | 'medicine' | 'treat';
  points: number;
  matched: boolean;
}

export interface NetPuzzleState {
  pieces: NetPiece[];
  otterPosition: Vector2;
  movesUsed: number;
  bestMoves: number;
}

export interface NetPiece {
  id: string;
  position: Vector2;
  connections: string[]; // IDs of connected pieces
  canRemove: boolean;
  removed: boolean;
}

// Combined game progress (stored in localStorage)
export interface GameProgress {
  riverRush: RiverRushProgress;
  rescueMission: RescueMissionProgress;
}

export const defaultGameProgress: GameProgress = {
  riverRush: {
    highScore: 0,
    totalDistance: 0,
    totalFoodCollected: 0,
    gamesPlayed: 0,
    unlockedOtters: ['sea-otter'],
    fishTokens: 0,
    upgrades: {
      thickerFur: 0,
      biggerAppetite: 0,
      quickReflexes: 0,
      luckyOtter: 0,
    },
  },
  rescueMission: {
    rescuedOtters: [],
    totalRescues: 0,
    currentCases: [],
    facilityUpgrades: {
      medicalBay: 0,
      nursery: 0,
      trainingPool: 0,
      researchLab: 0,
    },
    rescuerRank: 'volunteer',
    stars: 0,
  },
};
